import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { Box, Typography } from "@mui/material";
import { DataTableCellValue, DataTableRowData, TableCellInputType } from "../components/data-table/types";
import { formatNullCellValue } from "../components/data-table/utils";
import { TableColumnFromApi } from "../hooks/use-config-context/types";
import { underscoreToTitleCase } from "./index";
import { WpFieldConfig } from "../hooks/use-config/types";
import { FieldsConfigResponse } from "../hooks/use-config-context/types";

/**
 * Creates icon-based boolean formatter
 */
const createBooleanFormatter = () => {
  const checkIcon = (
    <CheckIcon sx={{ color: (theme) => theme.palette.success.main }} />
  );
  const closeIcon = (
    <CloseIcon sx={{ color: (theme) => theme.palette.error.main }} />
  );

  return (value: DataTableCellValue) => {
    if (value === "1" || value === 1 || value === true || value === "Yes") {
      return checkIcon;
    } else {
      return closeIcon;
    }
  };
};

/**
 * Creates formatter for form/object field values (JSON strings)
 */
const createFormFormatter = () => {
  return (value: DataTableCellValue) => {
    if (typeof value === "string" && value !== "") {
      try {
        return Object.entries(JSON.parse(value)).map(([key, val]) => {
          return (
            <Box key={key}>
              <Typography variant="caption" fontWeight={600}>
                {underscoreToTitleCase(key)}:
              </Typography>{" "}
              <Typography variant="caption">
                {formatNullCellValue(val)}
              </Typography>
            </Box>
          );
        });
      } catch (error) {
        return formatNullCellValue(value);
      }
    }
    return formatNullCellValue(value);
  };
};

/**
 * Creates formatter for select field values
 */
const createSelectFormatter = (fieldConfig: WpFieldConfig) => {
  return (value: DataTableCellValue) => {
    if (typeof value === "string" && value !== "" && value.includes("{")) {
      try {
        const parsedValue = JSON.parse(value);
        return parsedValue?.name ?? "";
      } catch (error) {
        return formatNullCellValue(value);
      }
    }
    if (fieldConfig?.options) {
      return (
        (fieldConfig.options as any[]).find(
          (option) =>
            option.value === value || option.value?.toString() === value
        )?.label || formatNullCellValue(value)
      );
    }
    return formatNullCellValue(value);
  };
};

/**
 * Builds cell formatters for table columns based on their input type
 * @param columns - Table column definitions
 * @param config - Field configuration (options, etc.)
 * @param checkDisplayType - Whether to check displayType field for boolean formatting (useful for some tables)
 * @returns Record of formatters keyed by column ID
 */
export const buildTableCellFormatters = (
  columns: TableColumnFromApi<any>[],
  config: FieldsConfigResponse,
  checkDisplayType: boolean = false
): Record<string, (value: DataTableCellValue, rowData?: DataTableRowData) => React.ReactNode> => {
  const booleanFormatter = createBooleanFormatter();
  const formFormatter = createFormFormatter();

  const formatters = columns.reduce((acc, column) => {
    const fieldConfig = config[column.id];
    
    const isBooleanType = column.inputType === TableCellInputType.Boolean ||
      (checkDisplayType && column.displayType === TableCellInputType.Boolean);
    
    if (isBooleanType) {
      acc[column.id] = booleanFormatter;
    }
    if (column.inputType === TableCellInputType.Form) {
      acc[column.id] = formFormatter;
    }
    if (column.inputType === TableCellInputType.Select) {
      acc[column.id] = createSelectFormatter(fieldConfig);
    }
    if (column.inputType === TableCellInputType.UserInput) {
      acc[column.id] = createSelectFormatter(fieldConfig);
    }
    return acc;
  }, {} as Record<string, (value: DataTableCellValue, rowData?: DataTableRowData) => React.ReactNode>);

  return formatters;
};
